import React from 'react';
import moment from 'moment';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import PropTypes from 'prop-types';

import AnalyticsPropTypes from 'propTypeModels/analytics';
import { formatNumber } from 'utils/currencyFormat';

import styles from './StayChart.module.scss';

const MONTH_LABELS = [
  'Thg 1', 'Thg 2', 'Thg 3', 'Thg 4',
  'Thg 5', 'Thg 6', 'Thg 7', 'Thg 8',
  'Thg 9', 'Thg 10', 'Thg 11', 'Thg 12',
];

const BAR_COLORS = {
  current: '#165DFF',
  lastYear: '#00BBFF',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  const year = payload[0]?.payload?.year;
  const monthLabel = year ? `${label}, ${year}` : label;

  return (
    <div className={styles.Tooltip}>
      <p className={styles.TooltipTitle}>{monthLabel}</p>
      {payload.map((entry) => (
        <div key={entry.dataKey} className={styles.TooltipRow}>
          <span
            className={styles.TooltipDot}
            style={{ backgroundColor: entry.color }}
          />
          <span className={styles.TooltipLabel}>
            {entry.name}
            :
          </span>
          <span className={styles.TooltipValue}>{formatNumber(entry.value)}</span>
        </div>
      ))}
    </div>
  );
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.shape({
    dataKey: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.number,
    color: PropTypes.string,
    payload: PropTypes.shape({
      year: PropTypes.number,
    }),
  })),
  label: PropTypes.string,
};

CustomTooltip.defaultProps = {
  active: false,
  payload: [],
  label: '',
};

function StayChart({ data, compareLabel = 'năm ngoái' }) {
  const formattedData = React.useMemo(() => {
    const now = moment();
    const currentCalendarYear = now.year();
    const currentMonth = now.month() + 1; // 1-indexed

    const currentList = Array.isArray(data) ? data : (data?.data || []);
    const compareList = Array.isArray(data) ? data : (data?.compareData || []);
    const isOldArray = Array.isArray(data);

    return MONTH_LABELS.map((label, index) => {
      const monthIndex = index + 1;

      // If the month is in the future of the current calendar year,
      // the "current" data is actually from the previous calendar year.
      const currentYearForMonth = monthIndex <= currentMonth
        ? currentCalendarYear
        : currentCalendarYear - 1;

      const lastYearForMonth = currentYearForMonth - 1;

      const currentYearNights = currentList.reduce((total, item) => {
        const itemMoment = moment(item.startedAt);
        const matchesYear = isOldArray
          ? itemMoment.year() === currentYearForMonth
          : true;
        if (
          matchesYear
          && itemMoment.month() + 1 === monthIndex
        ) {
          return total + (item.nights || 0);
        }
        return total;
      }, 0);

      const lastYearNights = compareList.reduce((total, item) => {
        const itemMoment = moment(item.startedAt);
        const matchesYear = isOldArray
          ? itemMoment.year() === lastYearForMonth
          : true;
        if (
          matchesYear
          && itemMoment.month() + 1 === monthIndex
        ) {
          return total + (item.nights || 0);
        }
        return total;
      }, 0);

      return {
        name: label,
        year: currentYearForMonth,
        nights: currentYearNights,
        lastYearNights,
      };
    });
  }, [data]);

  return (
    <div className={styles.Container}>
      <ResponsiveContainer width="100%" height={380}>
        <BarChart
          data={formattedData}
          margin={{
            top: 10,
            right: 10,
            left: 0,
            bottom: 0,
          }}
          barCategoryGap="20%"
          barGap={2}
        >
          <CartesianGrid
            strokeDasharray="4 4"
            vertical={false}
            stroke="#E5E6EB"
          />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 13, fill: '#86909C' }}
            axisLine={{ stroke: '#E5E6EB' }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 13, fill: '#86909C' }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            domain={[0, 'auto']}
            tickFormatter={formatNumber}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
          />
          <Bar
            dataKey="nights"
            name="Đêm phòng"
            fill={BAR_COLORS.current}
            barSize={36}
          />
          <Bar
            dataKey="lastYearNights"
            name={`Đêm phòng ${compareLabel}`}
            fill={BAR_COLORS.lastYear}
            barSize={36}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

StayChart.propTypes = {
  data: PropTypes.arrayOf(AnalyticsPropTypes.performanceChart)
    .isRequired,
  compareLabel: PropTypes.string,
};

StayChart.defaultProps = {
  compareLabel: 'năm ngoái',
};

export default React.memo(StayChart);
